import { useState } from "react";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";

import uiConf from "../../config/uiConf";
import notificationState from "../../states/notificationState";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogHeaderWithClose from "../dialog/functional/DialogHeaderWithClose";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogContent from "../dialog/functional/DialogContent";
import DialogActions from "../dialog/functional/DialogActions";

import AutoFilterForm from "../filter/functional/AutoFilterForm";
import FilterApplyButton from "../input/FilterApplyButton";
import FilterClearButton from "../input/FilterClearButton";
import FilterCancelButton from "../input/FilterCancelButton";

function FilterDialog ( props ) {

    const { id, title, schema, filter, filterValue, moveable, resizable, fullWidth, maxWidth, onInitialized, onFormInitialized, onApply, onClear, onCancel, onFilterChangeValue, ...others } = props;

    const { t } = useTranslation ();
    const setNotification = useSetRecoilState ( notificationState );

    const [ , setInstance ] = useState ();
    const [ filterApplyButton, setFilterApplyButton ] = useState ();

    const validateDateRange = ( filterValue ) => {

        if ( !filterValue || !schema ) return null;

        for ( const filter of filterValue ) {

            if ( filter && filter.condition1 && filter.condition2 ) {

                const startValue = filter.condition1.value;
                const endValue = filter.condition2.value;

                if ( startValue instanceof Date && endValue instanceof Date ) {

                    if ( startValue.getTime () > endValue.getTime () ) {

                        const column = schema.columns.find ( ( col ) => col.name === filter.binding );
                        const columnName = column ? t ( `columnName.${column.displayName || column.name}` ) : filter.binding;
                        const isDateTime = column && ( column.formItemType === "datetime" || column.formItemType === "time" || ( column.type && ( column.type.startsWith ( "datetime" ) || column.type.startsWith ( "time" ) ) ) );

                        return { columnName, isDateTime };

                    }

                }

            }

        }

        return null;

    };

    const onValidateFilter = ( filterValue ) => {

        const result = validateDateRange ( filterValue );

        if ( result !== null ) {

            const msgKey = result.isDateTime ? "warning.startDateTimeMustBeBeforeEndDateTime" : "warning.startDateMustBeBeforeEndDate";

            setNotification ( {
                severity: "warning",
                msg: t ( msgKey, { columnName: result.columnName } ),
                open: true,
            } );

            return false;

        }

        return true;

    };

    const onApplyFilter = ( filterValue ) => {

        if ( onApply ) {

            onApply ( filterValue );

        }

        setInstance ( ( instance ) => {

            instance.hide ();
            return instance;

        } );

    };

    const onClearFilter = () => {

        if ( onClear ) {

            onClear ();

        }

    }

    const closeFilterDialog = () => {

        setInstance ( ( instance ) => {

            instance.hide ();
            return instance;

        } );

    }

    const onDialogInitialized = ( instance ) => {

        setInstance ( instance );

        if ( onInitialized ) {

            onInitialized ( instance );

        }

    };

    const onFilterFormInitialized = ( filterForm ) => {

        if ( onFormInitialized ) {

            onFormInitialized ( filterForm );

        }
    };

    const onFilterApplyButtonInitialized = ( button ) => {

        setFilterApplyButton ( button );

    };

    return (

        <Dialog
            id={id}
            title={title}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth={maxWidth ? maxWidth : "md"}
            onInitialized={onDialogInitialized}
            onClose={onCancel}
            {...others}
        >

            {uiConf.modalWithCloseButton ? (
                <DialogHeaderWithClose>
                    <DialogTitle>
                        {title}
                    </DialogTitle>
                </DialogHeaderWithClose>
            ) : (
                <DialogHeader>
                    <DialogTitle>
                        {title}
                    </DialogTitle>
                </DialogHeader>
            ) }

            <DialogContent style={{ padding: 0, marginLeft: 0, marginRight: 0}} dividers >
                <AutoFilterForm
                    id={id+"-filter-form"}
                    className="autoFilterForm"
                    schema={schema}
                    filterValue={filterValue?cloneDeep(filterValue):filterValue} // object deep copy - 내부에서 객체 멤버가 수정되어 버그 발생
                    filterApplyButton={filterApplyButton}
                    onInitialized={onFilterFormInitialized}
                />
            </DialogContent>

            <DialogActions {...others} >
                <FilterApplyButton
                    id={id+"-filter-apply-button"}
                    className="filterApplyButton"
                    schema={schema}
                    filter={filter}
                    color="primary"
                    onValidate={onValidateFilter}
                    onApply={onApplyFilter}
                    onInitialized={onFilterApplyButtonInitialized}
                    onFilterChangeValue={onFilterChangeValue}
                />
                <FilterClearButton
                    id={id+"-filter-clear-button"}
                    className="filterClearButton"
                    color="primary"
                    onClear={onClearFilter}
                />
                <FilterCancelButton
                    id={id+"-filter-cancel-button"}
                    className="filterCancelButton"
                    color="secondary"
                    onCancel={closeFilterDialog}
                />                                
            </DialogActions>

        </Dialog>

    );

}

export default FilterDialog;
